import { useQuery } from "@tanstack/react-query";

import { SIGNED_URL_TTL_SECONDS, signedAttachmentUrl } from "./attachments";

/**
 * Resolve a stored attachment to a currently-valid URL.
 *
 * Since the bucket became private, the `src` saved on a record is a signed URL
 * that expires, so it cannot be rendered directly on a page opened later. The
 * durable reference is `path`; this re-signs it on demand and refreshes ahead
 * of expiry, so a long-lived tab does not degrade into broken images.
 *
 * Records created before isolation have no `path`, only an old public `src`.
 * Those fall back to `src` rather than rendering nothing: the file is either
 * still reachable or visibly broken, and silently blanking it would look like
 * data loss.
 */
export const useSignedAttachment = (
  path: string | undefined,
  fallbackSrc?: string,
) => {
  const { data } = useQuery({
    queryKey: ["attachment-url", path],
    enabled: Boolean(path),
    // Re-sign a little before the URL lapses.
    staleTime: (SIGNED_URL_TTL_SECONDS - 300) * 1000,
    refetchInterval: (SIGNED_URL_TTL_SECONDS - 300) * 1000,
    queryFn: () => signedAttachmentUrl(path!),
  });

  return data ?? fallbackSrc ?? "";
};
